import {useState} from 'react'

import {useDecision} from '@s-ui/pde'
import {useSuiContext} from '@s-ui/react-context'
import useControlledState from '@s-ui/react-hooks/lib/useControlledState/index.js'
import {useLocation} from '@s-ui/react-router'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import {iaSearchNotificationTypes, iaSearchNotificationTypesDictionary} from '@adv-mt/mt-notifications-ai'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {DEFAULT_IS_LOADING, FORCE_FLAG} from './config.js'

const useAiSearch = ({isLoading: isLoadingFromProps, listingSectionId, pageName}) => {
  const [isLoading, setIsLoading] = useControlledState(isLoadingFromProps, DEFAULT_IS_LOADING)
  const [iaSearchNotificationType, setIASearchNotificationType] = useState()

  const {domain} = useSuiContext()

  const {pathname} = useLocation()

  const delayToNextFrame = useDelayToNextFrame()

  const {track} = useAnalyticsTrack()

  const config = domain.get('config')

  const {TRACKING_EVENT_NAMES} = config.get('tracking')

  const {enabled: isAiSearchBarRemakeEnabled, variationKey: aiSearchBarRemakeVariant} = useDecision(
    'ff_backend_buyer_ai_search_bar_remake'
  )

  const handleSearchByAI = async ({query}) => {
    setIsLoading(true, FORCE_FLAG)
    delayToNextFrame(async () => {
      const variant = isAiSearchBarRemakeEnabled ? aiSearchBarRemakeVariant : null
      const [searchRequestError, searchRequestReponse] = await domain
        .get(
          // Temporal hack until renting flow uses the same use case
          // Related tech-debt: https://jira.ets.mpi-internal.com/browse/MTR-94416
          listingSectionId === '6'
            ? 'get_renting_search_request_from_ai_search_use_case'
            : 'get_search_request_from_ai_search_use_case'
        )
        .execute({listingSectionId, query, url: pathname, variant})

      if (searchRequestError) {
        const AISearchUserTrackingData = await domain.get('get_event_tracking_data_use_case').execute({
          event: TRACKING_EVENT_NAMES.AI_SEARCH_USED,
          pageName,
          query,
          requestStatus: searchRequestError.code
        })

        track(AISearchUserTrackingData)

        setIsLoading(false, FORCE_FLAG)
        return setIASearchNotificationType(iaSearchNotificationTypesDictionary[searchRequestError.code])
      }

      const AISearchUserTrackingData = await domain.get('get_event_tracking_data_use_case').execute({
        event: TRACKING_EVENT_NAMES.AI_SEARCH_USED,
        pageName,
        query
      })

      track(AISearchUserTrackingData)

      setIASearchNotificationType(iaSearchNotificationTypes.SUCCESS)

      await domain.get('get_url_params_search_use_case').execute({
        listingSectionId: searchRequestReponse.listingSectionId,
        search: searchRequestReponse.searchForFilters
      })
    })
  }

  const handleCloseNotification = () => {
    setIASearchNotificationType()
  }

  return {
    isLoading,
    notificationType: iaSearchNotificationType,
    onCloseNotification: handleCloseNotification,
    onSearch: handleSearchByAI
  }
}

export default useAiSearch
